// ============================================================================
// Store and Collection Types
// ============================================================================

export type StoreType = "ios" | "android";
export type CollectionType = "free" | "paid" | "grossing";
export type DeviceType = "iphone" | "ipad";

/**
 * Rank badge for search results - shows if app appears in rankings
 */
export interface RankBadge {
  rank: number;
  collection: CollectionType;
}

// ============================================================================
// API Response Types
// ============================================================================

/**
 * Response from GET /api/rankings/all
 */
export interface RankingsAllResponse {
  store: StoreType;
  country: string;
  category: string;
  updatedAt: string; // ISO timestamp
  free: RankedApp[];
  paid: RankedApp[];
  grossing: RankedApp[];
}

/**
 * App item in rankings list
 */
export interface RankedApp {
  rank: number;
  id: string; // App ID (numeric for iOS, package name for Android)
  appId: string; // Bundle/package ID
  name: string;
  icon: string; // URL to app icon
  developer: string;
  price: string; // "Free" or "$X.XX"
  rating: number; // 0-5
  installs?: string; // Android only, e.g., "10,000,000+"
}

/**
 * Response from GET /api/categories
 */
export type CategoriesResponse = Category[];

export interface Category {
  id: string;
  name: string;
}

/**
 * Response from GET /api/countries
 */
export type CountriesResponse = Country[];

export interface Country {
  code: string; // e.g., "us", "gb", "de"
  name: string; // e.g., "United States"
}

/**
 * Search result item (minimal data)
 */
export interface AppSearchResult {
  id: string; // iOS: numeric ID, Android: package name
  name: string;
  developer: string;
  icon: string; // App icon URL
}

/**
 * Response from GET /api/search
 */
export interface AppSearchResponse {
  store: StoreType;
  country: string;
  term: string;
  updatedAt: string;
  results: AppSearchResult[];
}

/**
 * Response from GET /api/app
 */
export interface AppDetailResponse {
  id: string;
  appId: string;
  name: string;
  description: string;
  descriptionHTML?: string; // Android only
  summary?: string; // Android only
  icon: string;
  headerImage?: string; // Android only
  screenshots: string[];
  ipadScreenshots?: string[]; // iOS only
  video?: string; // Android only
  developer: string;
  developerId: string | number;
  developerEmail?: string; // Android only
  developerWebsite?: string;
  genre?: string; // Android primary genre
  genres?: string[]; // iOS genre list
  primaryGenre?: string; // iOS
  price: string;
  free: boolean;
  currency?: string;
  rating: number;
  ratings?: number; // Android total ratings count
  reviews: number;
  histogram?: Record<string, number>; // Android rating distribution
  installs?: string; // Android only
  version: string;
  size?: string; // iOS only
  released: string;
  updated: string | number;
  androidVersion?: string; // Android only
  contentRating: string;
  releaseNotes?: string; // iOS only
  recentChanges?: string; // Android only
  languages?: string[]; // iOS only
  offersIAP?: boolean; // Android only
  IAPRange?: string; // Android only, e.g., "$0.99 - $99.99 per item"
  adSupported?: boolean; // Android only
  url: string; // Store URL
}

// ============================================================================
// Game Subcategories
// ============================================================================

export const GAME_SUBCATEGORIES = [
  { id: "GAME_ACTION", name: "Action" },
  { id: "GAME_ADVENTURE", name: "Adventure" },
  { id: "GAME_ARCADE", name: "Arcade" },
  { id: "GAME_BOARD", name: "Board" },
  { id: "GAME_CARD", name: "Card" },
  { id: "GAME_CASINO", name: "Casino" },
  { id: "GAME_CASUAL", name: "Casual" },
  { id: "GAME_EDUCATIONAL", name: "Educational" },
  { id: "GAME_MUSIC", name: "Music" },
  { id: "GAME_PUZZLE", name: "Puzzle" },
  { id: "GAME_RACING", name: "Racing" },
  { id: "GAME_ROLE_PLAYING", name: "Role Playing" },
  { id: "GAME_SIMULATION", name: "Simulation" },
  { id: "GAME_SPORTS", name: "Sports" },
  { id: "GAME_STRATEGY", name: "Strategy" },
  { id: "GAME_TRIVIA", name: "Trivia" },
  { id: "GAME_WORD", name: "Word" },
];

// ============================================================================
// Countries
// ============================================================================

/**
 * iOS App Store countries (175 countries)
 */
export const IOS_COUNTRIES: Country[] = [
  { code: "us", name: "United States" },
  { code: "gb", name: "United Kingdom" },
  { code: "cn", name: "China" },
  { code: "ca", name: "Canada" },
  { code: "de", name: "Germany" },
  { code: "jp", name: "Japan" },
  { code: "fr", name: "France" },
  { code: "it", name: "Italy" },
  { code: "es", name: "Spain" },
  { code: "au", name: "Australia" },
  { code: "af", name: "Afghanistan" },
  { code: "al", name: "Albania" },
  { code: "dz", name: "Algeria" },
  { code: "ao", name: "Angola" },
  { code: "ai", name: "Anguilla" },
  { code: "ag", name: "Antigua & Barbuda" },
  { code: "ar", name: "Argentina" },
  { code: "am", name: "Armenia" },
  { code: "at", name: "Austria" },
  { code: "az", name: "Azerbaijan" },
  { code: "bs", name: "Bahamas" },
  { code: "bh", name: "Bahrain" },
  { code: "bb", name: "Barbados" },
  { code: "by", name: "Belarus" },
  { code: "be", name: "Belgium" },
  { code: "bz", name: "Belize" },
  { code: "bj", name: "Benin" },
  { code: "bm", name: "Bermuda" },
  { code: "bt", name: "Bhutan" },
  { code: "bo", name: "Bolivia" },
  { code: "ba", name: "Bosnia & Herzegovina" },
  { code: "bw", name: "Botswana" },
  { code: "br", name: "Brazil" },
  { code: "vg", name: "British Virgin Islands" },
  { code: "bn", name: "Brunei" },
  { code: "bg", name: "Bulgaria" },
  { code: "bf", name: "Burkina Faso" },
  { code: "kh", name: "Cambodia" },
  { code: "cm", name: "Cameroon" },
  { code: "cv", name: "Cape Verde" },
  { code: "ky", name: "Cayman Islands" },
  { code: "td", name: "Chad" },
  { code: "cl", name: "Chile" },
  { code: "co", name: "Colombia" },
  { code: "cg", name: "Congo" },
  { code: "cr", name: "Costa Rica" },
  { code: "ci", name: "Côte d'Ivoire" },
  { code: "hr", name: "Croatia" },
  { code: "cy", name: "Cyprus" },
  { code: "cz", name: "Czechia" },
  { code: "dk", name: "Denmark" },
  { code: "dm", name: "Dominica" },
  { code: "do", name: "Dominican Republic" },
  { code: "cd", name: "DR Congo" },
  { code: "ec", name: "Ecuador" },
  { code: "eg", name: "Egypt" },
  { code: "sv", name: "El Salvador" },
  { code: "ee", name: "Estonia" },
  { code: "sz", name: "Eswatini" },
  { code: "fm", name: "Federated States Of Micronesia" },
  { code: "fj", name: "Fiji" },
  { code: "fi", name: "Finland" },
  { code: "ga", name: "Gabon" },
  { code: "gm", name: "Gambia" },
  { code: "ge", name: "Georgia" },
  { code: "gh", name: "Ghana" },
  { code: "gr", name: "Greece" },
  { code: "gd", name: "Grenada" },
  { code: "gt", name: "Guatemala" },
  { code: "gw", name: "Guinea-Bissau" },
  { code: "gy", name: "Guyana" },
  { code: "hn", name: "Honduras" },
  { code: "hk", name: "Hong Kong" },
  { code: "hu", name: "Hungary" },
  { code: "is", name: "Iceland" },
  { code: "in", name: "India" },
  { code: "id", name: "Indonesia" },
  { code: "iq", name: "Iraq" },
  { code: "ie", name: "Ireland" },
  { code: "il", name: "Israel" },
  { code: "jm", name: "Jamaica" },
  { code: "jo", name: "Jordan" },
  { code: "kz", name: "Kazakhstan" },
  { code: "ke", name: "Kenya" },
  { code: "xk", name: "Kosovo" },
  { code: "kw", name: "Kuwait" },
  { code: "kg", name: "Kyrgyzstan" },
  { code: "la", name: "Laos" },
  { code: "lv", name: "Latvia" },
  { code: "lb", name: "Lebanon" },
  { code: "lr", name: "Liberia" },
  { code: "ly", name: "Libya" },
  { code: "lt", name: "Lithuania" },
  { code: "lu", name: "Luxembourg" },
  { code: "mo", name: "Macao" },
  { code: "mg", name: "Madagascar" },
  { code: "mw", name: "Malawi" },
  { code: "my", name: "Malaysia" },
  { code: "mv", name: "Maldives" },
  { code: "ml", name: "Mali" },
  { code: "mt", name: "Malta" },
  { code: "mr", name: "Mauritania" },
  { code: "mu", name: "Mauritius" },
  { code: "mx", name: "Mexico" },
  { code: "md", name: "Moldova" },
  { code: "mn", name: "Mongolia" },
  { code: "me", name: "Montenegro" },
  { code: "ms", name: "Montserrat" },
  { code: "ma", name: "Morocco" },
  { code: "mz", name: "Mozambique" },
  { code: "mm", name: "Myanmar" },
  { code: "na", name: "Namibia" },
  { code: "nr", name: "Nauru" },
  { code: "np", name: "Nepal" },
  { code: "nl", name: "Netherlands" },
  { code: "nz", name: "New Zealand" },
  { code: "ni", name: "Nicaragua" },
  { code: "ne", name: "Niger" },
  { code: "ng", name: "Nigeria" },
  { code: "mk", name: "North Macedonia" },
  { code: "no", name: "Norway" },
  { code: "om", name: "Oman" },
  { code: "pk", name: "Pakistan" },
  { code: "pw", name: "Palau" },
  { code: "pa", name: "Panama" },
  { code: "pg", name: "Papua New Guinea" },
  { code: "py", name: "Paraguay" },
  { code: "pe", name: "Peru" },
  { code: "ph", name: "Philippines" },
  { code: "pl", name: "Poland" },
  { code: "pt", name: "Portugal" },
  { code: "qa", name: "Qatar" },
  { code: "ro", name: "Romania" },
  { code: "ru", name: "Russia" },
  { code: "rw", name: "Rwanda" },
  { code: "st", name: "São Tomé & Príncipe" },
  { code: "sa", name: "Saudi Arabia" },
  { code: "sn", name: "Senegal" },
  { code: "rs", name: "Serbia" },
  { code: "sc", name: "Seychelles" },
  { code: "sl", name: "Sierra Leone" },
  { code: "sg", name: "Singapore" },
  { code: "sk", name: "Slovakia" },
  { code: "si", name: "Slovenia" },
  { code: "sb", name: "Solomon Islands" },
  { code: "za", name: "South Africa" },
  { code: "kr", name: "South Korea" },
  { code: "lk", name: "Sri Lanka" },
  { code: "kn", name: "St. Kitts & Nevis" },
  { code: "lc", name: "St. Lucia" },
  { code: "vc", name: "St. Vincent & Grenadines" },
  { code: "sr", name: "Suriname" },
  { code: "se", name: "Sweden" },
  { code: "ch", name: "Switzerland" },
  { code: "tw", name: "Taiwan" },
  { code: "tj", name: "Tajikistan" },
  { code: "tz", name: "Tanzania" },
  { code: "th", name: "Thailand" },
  { code: "to", name: "Tonga" },
  { code: "tt", name: "Trinidad & Tobago" },
  { code: "tn", name: "Tunisia" },
  { code: "tr", name: "Turkey" },
  { code: "tm", name: "Turkmenistan" },
  { code: "tc", name: "Turks & Caicos Islands" },
  { code: "ug", name: "Uganda" },
  { code: "ua", name: "Ukraine" },
  { code: "ae", name: "United Arab Emirates" },
  { code: "uy", name: "Uruguay" },
  { code: "uz", name: "Uzbekistan" },
  { code: "vu", name: "Vanuatu" },
  { code: "ve", name: "Venezuela" },
  { code: "vn", name: "Vietnam" },
  { code: "ye", name: "Yemen" },
  { code: "zm", name: "Zambia" },
  { code: "zw", name: "Zimbabwe" },
];

/**
 * Google Play countries (155 countries)
 */
export const ANDROID_COUNTRIES: Country[] = [
  { code: "us", name: "United States" },
  { code: "gb", name: "United Kingdom" },
  { code: "cn", name: "China" },
  { code: "ca", name: "Canada" },
  { code: "de", name: "Germany" },
  { code: "jp", name: "Japan" },
  { code: "fr", name: "France" },
  { code: "it", name: "Italy" },
  { code: "es", name: "Spain" },
  { code: "au", name: "Australia" },
  { code: "al", name: "Albania" },
  { code: "dz", name: "Algeria" },
  { code: "ao", name: "Angola" },
  { code: "ag", name: "Antigua & Barbuda" },
  { code: "ar", name: "Argentina" },
  { code: "am", name: "Armenia" },
  { code: "at", name: "Austria" },
  { code: "az", name: "Azerbaijan" },
  { code: "bs", name: "Bahamas" },
  { code: "bh", name: "Bahrain" },
  { code: "by", name: "Belarus" },
  { code: "be", name: "Belgium" },
  { code: "bz", name: "Belize" },
  { code: "bj", name: "Benin" },
  { code: "bm", name: "Bermuda" },
  { code: "bo", name: "Bolivia" },
  { code: "ba", name: "Bosnia & Herzegovina" },
  { code: "bw", name: "Botswana" },
  { code: "br", name: "Brazil" },
  { code: "vg", name: "British Virgin Islands" },
  { code: "bg", name: "Bulgaria" },
  { code: "bf", name: "Burkina Faso" },
  { code: "kh", name: "Cambodia" },
  { code: "cm", name: "Cameroon" },
  { code: "cv", name: "Cape Verde" },
  { code: "ky", name: "Cayman Islands" },
  { code: "cl", name: "Chile" },
  { code: "co", name: "Colombia" },
  { code: "cr", name: "Costa Rica" },
  { code: "ci", name: "Côte d'Ivoire" },
  { code: "hr", name: "Croatia" },
  { code: "cy", name: "Cyprus" },
  { code: "cz", name: "Czechia" },
  { code: "dk", name: "Denmark" },
  { code: "do", name: "Dominican Republic" },
  { code: "ec", name: "Ecuador" },
  { code: "eg", name: "Egypt" },
  { code: "sv", name: "El Salvador" },
  { code: "ee", name: "Estonia" },
  { code: "fm", name: "Federated States Of Micronesia" },
  { code: "fj", name: "Fiji" },
  { code: "fi", name: "Finland" },
  { code: "ga", name: "Gabon" },
  { code: "ge", name: "Georgia" },
  { code: "gh", name: "Ghana" },
  { code: "gr", name: "Greece" },
  { code: "gt", name: "Guatemala" },
  { code: "gw", name: "Guinea-Bissau" },
  { code: "hn", name: "Honduras" },
  { code: "hk", name: "Hong Kong" },
  { code: "hu", name: "Hungary" },
  { code: "is", name: "Iceland" },
  { code: "in", name: "India" },
  { code: "id", name: "Indonesia" },
  { code: "iq", name: "Iraq" },
  { code: "ie", name: "Ireland" },
  { code: "il", name: "Israel" },
  { code: "jm", name: "Jamaica" },
  { code: "jo", name: "Jordan" },
  { code: "kz", name: "Kazakhstan" },
  { code: "ke", name: "Kenya" },
  { code: "kw", name: "Kuwait" },
  { code: "kg", name: "Kyrgyzstan" },
  { code: "la", name: "Laos" },
  { code: "lv", name: "Latvia" },
  { code: "lb", name: "Lebanon" },
  { code: "lt", name: "Lithuania" },
  { code: "lu", name: "Luxembourg" },
  { code: "mo", name: "Macao" },
  { code: "my", name: "Malaysia" },
  { code: "ml", name: "Mali" },
  { code: "mt", name: "Malta" },
  { code: "mu", name: "Mauritius" },
  { code: "mx", name: "Mexico" },
  { code: "md", name: "Moldova" },
  { code: "ma", name: "Morocco" },
  { code: "mz", name: "Mozambique" },
  { code: "mm", name: "Myanmar" },
  { code: "na", name: "Namibia" },
  { code: "np", name: "Nepal" },
  { code: "nl", name: "Netherlands" },
  { code: "nz", name: "New Zealand" },
  { code: "ni", name: "Nicaragua" },
  { code: "ne", name: "Niger" },
  { code: "ng", name: "Nigeria" },
  { code: "mk", name: "North Macedonia" },
  { code: "no", name: "Norway" },
  { code: "om", name: "Oman" },
  { code: "pk", name: "Pakistan" },
  { code: "pa", name: "Panama" },
  { code: "pg", name: "Papua New Guinea" },
  { code: "py", name: "Paraguay" },
  { code: "pe", name: "Peru" },
  { code: "ph", name: "Philippines" },
  { code: "pl", name: "Poland" },
  { code: "pt", name: "Portugal" },
  { code: "qa", name: "Qatar" },
  { code: "ro", name: "Romania" },
  { code: "ru", name: "Russia" },
  { code: "rw", name: "Rwanda" },
  { code: "sa", name: "Saudi Arabia" },
  { code: "sn", name: "Senegal" },
  { code: "rs", name: "Serbia" },
  { code: "sg", name: "Singapore" },
  { code: "sk", name: "Slovakia" },
  { code: "si", name: "Slovenia" },
  { code: "sb", name: "Solomon Islands" },
  { code: "za", name: "South Africa" },
  { code: "kr", name: "South Korea" },
  { code: "lk", name: "Sri Lanka" },
  { code: "se", name: "Sweden" },
  { code: "ch", name: "Switzerland" },
  { code: "tw", name: "Taiwan" },
  { code: "tj", name: "Tajikistan" },
  { code: "tz", name: "Tanzania" },
  { code: "th", name: "Thailand" },
  { code: "tt", name: "Trinidad & Tobago" },
  { code: "tn", name: "Tunisia" },
  { code: "tr", name: "Turkey" },
  { code: "tm", name: "Turkmenistan" },
  { code: "tc", name: "Turks & Caicos Islands" },
  { code: "ug", name: "Uganda" },
  { code: "ua", name: "Ukraine" },
  { code: "ae", name: "United Arab Emirates" },
  { code: "uy", name: "Uruguay" },
  { code: "uz", name: "Uzbekistan" },
  { code: "ve", name: "Venezuela" },
  { code: "vn", name: "Vietnam" },
  { code: "ye", name: "Yemen" },
  { code: "zm", name: "Zambia" },
  { code: "zw", name: "Zimbabwe" },
];
